/**
 * The 2026 redesign: which organiser screens show their new version.
 *
 * Every screen marked `ready` below shows its redesign to everyone (since
 * 15 October 2026, when the whole organiser side was done; the reviewer
 * allowlist and the REDESIGN_LIVE switch that staged the rollout are gone).
 * A screen marked `building` keeps its current version for everybody, so a
 * screen can be rebuilt in the open and flipped to `ready` when it is done.
 *
 * This module is pure so it can be tested without Clerk, cookies or a request.
 * The server-side entry point is lib/redesign/server.ts.
 */

/**
 * Every organiser-side screen the redesign covers.
 *
 * `ready` means: built, wired to the real route, and function-parity audited
 * against the live component (see docs/plans/redesign-function-parity.md). A
 * preview at /design-tokens is NOT enough: those run on invented data, and
 * ready means the real route renders the new screen with real data.
 *
 * Flipping a screen to `ready` puts it in front of every organiser.
 */
export type RedesignStatus = 'ready' | 'building';

export type RedesignScreen =
  // Event level, the thirteen tabs plus the two new destinations
  | 'event.setup'
  | 'event.branding'
  | 'event.registrants'
  | 'event.volunteers'
  | 'event.requests'
  | 'event.groups'
  | 'event.ancillaries'
  | 'event.schedule'
  | 'event.checkin'
  | 'event.scoring'
  | 'event.analytics'
  | 'event.comms'
  | 'event.fundraising'
  | 'event.tickets'
  | 'event.spectators'
  | 'event.photos'
  | 'event.overview'
  /** Redesign only: consent and safety records (health answers, signatures,
   *  guardian co-signatures, photo consent, emergency contacts) on one page. */
  | 'event.compliance'
  | 'event.vatReport'
  // Organiser level
  | 'org.home'
  | 'org.events'
  | 'org.finance'
  | 'org.volunteers'
  | 'org.registrants'
  | 'org.campaigns'
  | 'org.contacts'
  | 'org.settings'
  | 'org.billing'
  | 'org.account'
  | 'org.onboarding'
  // Create an event
  | 'create.wizard'
  /**
   * The chrome itself: the rail, the top bar, the organisation switcher.
   *
   * NOT a screen, and it cannot be gated like one. Turning it on puts every
   * tab body inside the new frame, whatever that tab's own flag says. Every
   * tab now has a v8 body, but a tab flipped back to `building` would show its
   * old body inside this frame.
   */
  | 'shell';

/**
 * The registry. Everything starts as `building`.
 *
 * Flipping one of these to `ready` is the deliberate act that puts a screen in
 * front of the reviewer. It should happen in the PR that finishes the screen,
 * never in a batch afterwards, so the flip and the screen are reviewed
 * together.
 */
export const REDESIGN_SCREENS: Record<RedesignScreen, RedesignStatus> = {
  // First slice 7 October 2026: the handoff's grouped rows (Still to do,
  // Done, Optional) around every existing section, each in the new colours.
  // Parity is total: the sections are the same components. Their insides are
  // converted to the v8 controls slice by slice.
  'event.setup': 'ready',
  // 9 October 2026: every other event screen wired on v8 (Branding, Schedule,
  // Check-in, Scoring, Analytics, Groups, Comms, VAT report; Volunteers and
  // Fundraising as their coming-soon pages). Each keeps the old look for
  // organisers not on the allowlist.
  'event.branding': 'ready',
  // Wired 22 September 2026. The redesign swaps the toolbar and the table;
  // the detail sheet is the same component both lists render, so nothing in
  // it can have been lost.
  'event.registrants': 'ready',
  'event.volunteers': 'ready',
  // Wired 23 September 2026. The parity audit against the live component
  // turned up the completed-status bug, which is fixed on both screens.
  'event.requests': 'ready',
  'event.groups': 'ready',
  // Wired 28 September 2026 (#1006): the list, figures, filters, the category
  // picker and the product window with sizes, stock and checkout questions.
  // Orders is on v8 too (FvdAncillaryOrdersList and its order window). Photo
  // pre-purchases moved to Photos (#994), which is also ready below, so the
  // two reach organisers together.
  'event.ancillaries': 'ready',
  'event.schedule': 'ready',
  'event.checkin': 'ready',
  'event.scoring': 'ready',
  'event.analytics': 'ready',
  'event.comms': 'ready',
  'event.fundraising': 'ready',
  // Wired 5 October 2026: the list on the shared table and the ticket window,
  // every field of the Setup form, saved through the same payload builder.
  // Setup keeps a pointer to it. Early bird and spectator tickets follow.
  'event.tickets': 'ready',
  // New on the redesign, 7 October 2026: one row per person with a spectator
  // ticket, from spectator_tickets. Only in the rail once the event sells one.
  'event.spectators': 'ready',
  // Wired 26 September 2026, tab by tab, and every tab (Photos, Sharing,
  // Sales, Privacy) is now on the v8 design.
  'event.photos': 'ready',
  // Wired 26 September 2026: countdown, entries, divisions and activity, with
  // the event header moving here from every other tab and the event name
  // moving into the rail. The task list ("Before event day") is #782.
  'event.overview': 'ready',
  // New on the redesign, 12 October 2026: every consent and safety record in
  // one place. Health responses moved here from Setup; Setup keeps the
  // questionnaire, the waiver documents and the age rules.
  'event.compliance': 'ready',
  'event.vatReport': 'ready',
  'org.home': 'ready',
  'org.events': 'ready',
  'org.finance': 'ready',
  'org.volunteers': 'ready',
  // The organisation-level "All registrants" screen is the Contacts view
  // (org.contacts): one row per person across every event, each opening their
  // entries on the event's own Registrants tab. There is no separate route to
  // build, so this follows org.contacts.
  'org.registrants': 'ready',
  'org.campaigns': 'ready',
  'org.contacts': 'ready',
  'org.settings': 'ready',
  'org.billing': 'ready',
  'org.account': 'ready',
  'org.onboarding': 'ready',
  // Wired 4 October 2026: the shell, Template, Basics, Format and Tickets on
  // the format registry, then Page (brand, look and address beside the real
  // page preview) and Review (publish checks and go live), all on v8.
  // Anyone the gate turns away keeps the previous wizard (LegacyWizard).
  'create.wizard': 'ready',
  // Wired 23 September 2026. Every event tab and organisation screen in this
  // registry now has its v8 body, so the frame no longer wraps any old-look screen for a
  // reviewer on the allowlist.
  'shell': 'ready',
};

/**
 * Parse `REDESIGN_ALLOWLIST`: comma or whitespace separated Clerk user ids.
 * Only the internal /design-tokens preview pages still use it (preview-access).
 */
export function parseAllowlist(raw: string | undefined | null): string[] {
  if (!raw) return [];
  return raw
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}


/**
 * Should this screen show its redesigned version?
 *
 * Everyone gets every screen marked `ready` (since 15 October 2026, when the
 * whole organiser side was ready; the allowlist and REDESIGN_LIVE are gone).
 * A screen marked `building` stays on its current version for everybody, so
 * a new screen can still be built in the open and flipped when it is done.
 */
export function shouldShowRedesign(screen: RedesignScreen): boolean {
  return REDESIGN_SCREENS[screen] === 'ready';
}

/** Progress, for the rollout status page and for the PR body. */
export function redesignProgress(): { ready: number; total: number; remaining: RedesignScreen[] } {
  const entries = Object.entries(REDESIGN_SCREENS) as Array<[RedesignScreen, RedesignStatus]>;
  const remaining = entries.filter(([, s]) => s !== 'ready').map(([k]) => k);
  return { ready: entries.length - remaining.length, total: entries.length, remaining };
}

/**
 * Whether a dashboard path is inside a single event, where the event page draws
 * its own rail and the shell stands aside. /dashboard/events/new/** is not:
 * creating an event happens in the organisation, and matching "new" as an id
 * left the create screens with no rail at all.
 */
export function isEventDetailPath(pathname: string | null | undefined): boolean {
  // The VAT report is a standalone page with no event rail of its own, so it
  // keeps the organisation rail rather than rendering with none.
  return /^\/dashboard\/events\/(?!new(?:\/|$))[^/]+(?:\/(?!vat-report(?:\/|$)).*)?$/.test(pathname ?? '');
}
